<script setup lang="ts">
    import { formatNumericText } from '@shared/utils/formatNumericText';
    import { type Question, QuestionType, QuestionReportType } from '../../SurveysTypes.d';

    defineProps<{
        question: Question;
        surveyResponseCount: number;
    }>();

    const emit = defineEmits<{
        showRespondents: [];
    }>();
</script>

<template>
    <ul class="list-unstyled indicator-list d-grid gap-lg" style="--indicator-font-size: var(--font-size-base)">
        <li>
            <span class="indicator-label">{{ $Resources.SurveyResponseCountLabel }}</span>
            <span class="indicator-status" :class="{ success: question.ResponseCount > 0 }"></span>
            {{
                formatNumericText(question.AnsweredResponseCount, {
                    largeNumbers: true,
                })
            }}
        </li>
        <li>
            <span class="indicator-label">{{ $Resources.SurveyRespondentCountLabel }}</span>
            <span class="indicator-status m-sm-r" :class="{ success: question.ResponseCount > 0 }"></span>
            <a v-if="question.ResponseCount > 0" href="javascript:void(0)" @click.prevent="emit('showRespondents')">
                {{
                    formatNumericText(question.RespondentCount, {
                        largeNumbers: true,
                    })
                }}
            </a>
            <template v-else>
                {{
                    formatNumericText(question.RespondentCount, {
                        largeNumbers: true,
                    })
                }}
            </template>
        </li>
        <li>
            <span class="indicator-label">{{ $Resources.SurveyQuestionResponsePercentageLabel }}</span>
            <span class="indicator-status" :class="{ success: question.ResponseCount > 0 }"></span>
            {{
                formatNumericText(surveyResponseCount === 0 ? 0 : (question.AnsweredResponseCount / surveyResponseCount) * 100, {
                    suffix: '%',
                })
            }}
        </li>
        <li v-if="question.ReportType !== QuestionReportType.Nps && question.AverageNumericValue">
            <span class="indicator-label">{{ $Resources.AverageLabel }}</span>
            {{
                formatNumericText(question.AverageNumericValue, {
                    precision: 2,
                })
            }}
            <small v-if="question.TotalResponseCount !== question.ResponseCount">
                {{
                    formatNumericText(question.TotalAverageNumericValue, {
                        precision: 2,
                        prefix: '(',
                        suffix: ')',
                    })
                }}
            </small>
        </li>
        <li v-if="[QuestionType.Single, QuestionType.Multiple, QuestionType.MultipleNumeric].includes(question.Type)">
            <span class="indicator-label">
                {{ question.Type === QuestionType.MultipleNumeric ? $Resources.ListStatisticsTotalLabel : $Resources.QuestionDimensionLabel }}
            </span>
            <span v-if="question.Type === QuestionType.MultipleNumeric">
                {{
                    formatNumericText(question.NumericResponseSum, {
                        largeNumbers: true,
                        precision: 2,
                    })
                }}
            </span>
            <span v-else>{{ question.Dimension?.Name || '-' }}</span>
        </li>
    </ul>
</template>
